import { EventType } from "@/analytics/analytics-events";
import { EventBarrier } from "@/components/common/event-barrier";
import { SupportedCADFileExtensions } from "@/components/common/point-cloud-file-upload-context/cad-upload-utils";
import { useElementFileUploadContext } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import {
  MAX_UPLOAD_CLOUD_FILE_SIZE_GB,
  SupportedPCFileExtensions,
  useCanImportPointCloud,
} from "@/components/common/point-cloud-file-upload-context/point-cloud-upload-utils";
import { useDataManagementContext } from "@/components/ui/data-management/data-management-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useDataManagementFileHandler } from "@/hooks/use-data-management-file-handler";
import { ImgSheetFileExtensionsWithDotsList } from "@/modes/create-area-mode/create-area-utils";
import { ImportCard } from "@/modes/create-area-mode/import-card";
import { ImportDroneDataCard } from "@/modes/create-area-mode/import-drone-data-card";
import { ImportScansCard } from "@/modes/create-area-mode/import-scans-card";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { changeMode } from "@/store/mode-slice";
import {
  resetImportSheetAreaId,
  setImportSheetAreaId,
} from "@/store/modes/import-sheet-mode-slice";
import { selectActiveArea } from "@/store/selections-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  selectCanUploadCad,
  selectCanUploadWithDataManagement,
  selectCanUsePhotogrammetry,
} from "@/store/subscription-features/subscription-features-selectors";
import {
  selectCurrentUser,
  selectHasWritePermission,
} from "@/store/user-selectors";
import { useImportAnnotations } from "@/utils/import-annotations-hook";
import { useImport2DPhotos } from "@/utils/use-import-2d-photos";
import { DataManagementDropzone } from "@faro-lotv/data-management";
import {
  AreaIcon,
  CadSmallIcon,
  cyan,
  Dialog,
  FaroText,
  FileDocumentIcon,
  FileImageIcon,
  GaussianSplattingIcon,
  grey,
  LayersIcon,
  MarkupPolygonIcon,
  NO_TRANSLATE_CLASS,
  PointCloudIcon,
  TranslateVar,
  useBreakpointMdUp,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { getFileExtension } from "@faro-lotv/foundation";
import { selectCompanyId, selectProjectId } from "@faro-lotv/project-source";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Stack } from "@mui/system";
import {
  MouseEventHandler,
  PropsWithChildren,
  RefObject,
  useCallback,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  ListSupportedCadFormats,
  SUPPORTED_CAD_EXTENSIONS_FOR_DRAG_AND_DROP,
} from "./supported-cad-formats";

/**
 * List of supported pc file extensions with the dots.
 * The dot is required in order to work with the accept property of the file input tag.
 */
const PcFileExtensionsList = Object.values(SupportedPCFileExtensions).map(
  (ext) => `.${ext}`,
);

/**
 * List of CAD file formats to be listed in the File dialog.
 */
const CadFileTypeListForFileInput = [
  SupportedCADFileExtensions.nwd,
  SupportedCADFileExtensions.nwc,
  SupportedCADFileExtensions.rvt,
  SupportedCADFileExtensions.ifc,
  SupportedCADFileExtensions.dwg,
  SupportedCADFileExtensions.dxf,
  SupportedCADFileExtensions.fbx,
];

const GaussianSplatsFileExtensionsList = [
  ".ply",
  ".spz",
  ".splat",
  ".ksplat",
  ".rad",
];
const AnnotationFileExtensionsList = [".csv"];
const PhotoFileExtensionsList = [".png", ".jpeg", ".jpg"];

/**
 * List of CAD file extensions to be listed in the File dialog.
 * We are only using a subset of the supported list as the filter would be too long otherwise.
 * The *.* should allow the user to import ANY file as CAD file anyway.
 * The dot is required in order to work with the accept property of the file input tag.
 */
const CADFileExtensionsListForFileInput = CadFileTypeListForFileInput.map(
  (type: SupportedCADFileExtensions) => `.${type}`,
);

/** Possible model types that can be uploaded using the options in the menu */
enum TypeOfFileBeingUploaded {
  pointcloud = "pointcloud",
  gaussiansplats = "gaussiansplats",
  cad = "cad",
}

/**
 * structure of arguments to be passed into OnFileChanged
 */
type OnFileChangedArgs = {
  /** file selected in OpenFile dialog */
  selectedFile: File;

  /** file extension */
  extension: string;

  /** callback to be called in case of success of upload */
  onSuccess(): void;

  /** callback to be called in case of failure of upload */
  onFailedUpload(): void;
};

/**
 * Display the list of recommended file sizes for CAD models.
 *
 * @returns A table with the recommended file sizes for CAD models.
 */
function RecommendedCadModelFileSizes(): JSX.Element {
  const color = grey[800];

  interface CadModelSizeInfo {
    nwdFileSize: string;
    rvtFileSize: string;
    expectedExperience: string;
  }
  const fileSizeInfos: CadModelSizeInfo[] = [];
  fileSizeInfos.push({
    nwdFileSize: "Up to 10MB",
    rvtFileSize: "Up to 100MB",
    expectedExperience: "Excellent",
  });
  fileSizeInfos.push({
    nwdFileSize: "10~20MB",
    rvtFileSize: "100~200MB",
    expectedExperience: "Good",
  });
  fileSizeInfos.push({
    nwdFileSize: "20~100MB",
    rvtFileSize: "200MB~1GB",
    expectedExperience: "Average",
  });
  fileSizeInfos.push({
    nwdFileSize: ">100MB",
    rvtFileSize: ">1GB",
    expectedExperience: "Not recommended (yet)",
  });

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ color }}>Navisworks file size</TableCell>
            <TableCell sx={{ color }}>Revit file size</TableCell>
            <TableCell sx={{ color }}>Expected experience</TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          sx={{ "& tr:nth-of-type(2n+1)": { backgroundColor: grey[100] } }}
        >
          {fileSizeInfos.map((fileSizeInfo) => (
            <TableRow key={fileSizeInfo.nwdFileSize}>
              <TableCell sx={{ color }}>{fileSizeInfo.nwdFileSize}</TableCell>
              <TableCell sx={{ color }}>{fileSizeInfo.rvtFileSize}</TableCell>
              <TableCell sx={{ color }}>
                {fileSizeInfo.expectedExperience}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

type ImportDataDialogProps = {
  /** Controls whether the import data dialog is open */
  open: boolean;
  /** Callback when the dialog should be closed */
  onClose(): void;
};

/**
 * @returns Overlay dialog that allows importing different kinds of data.
 *
 * !!! When adjusting this component, check if the Draft View's DraftImportDataDialog needs to be
 *     adjusted as well. !!!
 */
export function ImportDataDialog({
  open,
  onClose,
}: ImportDataDialogProps): JSX.Element | null {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  // Callbacks to be informed about a new file being selected for upload.
  const { setPointCloudFile, setGaussianSplatsFile, setCadFile } =
    useElementFileUploadContext();
  const [isFileSizeDialogOpen, setIsFileSizeDialogOpen] = useState(false);
  const [isFileFormatsDialogOpen, setIsFileFormatsDialogOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { setSheetFile } = useDataManagementContext();

  const [typeOfFileToUpload, setTypeOfFileToUpload] = useState<
    TypeOfFileBeingUploaded | undefined
  >();

  // hasWritePermission is already checked by the surrounding ImportBaseComponent, which returns
  // null for the component if false. Since the area import item doesn't require another permission
  // on top of that, the menu is never empty if hasWritePermission is fulfilled.
  const canImportPointCloud = useCanImportPointCloud();
  const canImportGaussianSplats = useAppSelector(
    selectHasFeature(Features.GaussianSplats),
  );
  const canImportCAD = useAppSelector(selectCanUploadCad);
  const canUploadWithDataManagement = useAppSelector(
    selectCanUploadWithDataManagement,
  );
  const canUsePhotogrammetry = useAppSelector(selectCanUsePhotogrammetry);
  const hasImportAnnotationsFeature = useAppSelector(
    selectHasFeature(Features.ImportAnnotations),
  );
  const activeArea = useAppSelector(selectActiveArea);
  const projectId = useAppSelector(selectProjectId);
  const isMdUp = useBreakpointMdUp();
  const import2DPhotos = useImport2DPhotos();
  const hasUnassigned2dPhotosFeature = useAppSelector(
    selectHasFeature(Features.Unassigned2dPhotos),
  );

  const fileInput = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line func-style -- FIXME
  const onUploadClicked = (): void => {
    onClose();
    fileInput.current?.click();
  };

  const importAnnotations = useImportAnnotations();

  /**
   * Display a popup dialog with information for user about size file limitations.
   */
  const displayFileSizeRecommendations = useCallback<MouseEventHandler>(
    (event) => {
      // Stop the event propagation to avoid triggering the onClick of the ImportCard.
      event.stopPropagation();
      setIsFileSizeDialogOpen(true);
    },
    [],
  );

  /**
   * Will display a popup dialog with the list of supported CAD formats.
   */
  const displayListOfSupportedCadFormats = useCallback<MouseEventHandler>(
    (event) => {
      // Stop the event propagation to avoid triggering the onClick of the ImportCard.
      event.stopPropagation();
      setIsFileFormatsDialogOpen(true);
    },
    [],
  );

  const onFileChanged = useCallback(
    ({
      extension,
      selectedFile,
      onFailedUpload,
      onSuccess,
    }: OnFileChangedArgs) => {
      let failedUpload = false;

      switch (typeOfFileToUpload) {
        case TypeOfFileBeingUploaded.pointcloud:
          if (PcFileExtensionsList.includes(extension)) {
            setPointCloudFile(selectedFile);
          } else {
            failedUpload = true;
          }
          break;
        case TypeOfFileBeingUploaded.gaussiansplats:
          setGaussianSplatsFile(selectedFile);
          break;
        case TypeOfFileBeingUploaded.cad:
          setCadFile(selectedFile);
          break;
        default:
          failedUpload = true;
          break;
      }

      // If the file type selected is not supported, call the onFailedUpload callback.
      if (failedUpload) {
        onFailedUpload();
      }

      onSuccess();
    },
    [setCadFile, setGaussianSplatsFile, setPointCloudFile, typeOfFileToUpload],
  );

  // File checking for DataManagementDropzone. The ImportScansCard has its own call.
  const {
    isCheckingFiles,
    setIsCheckingFiles,
    onFinishedCheckingFiles,
    fileTypesByCategory,
  } = useDataManagementFileHandler({
    projectId,
    from: "importScansDialog",
    onClose,
  });

  const cards = [];
  if (canImportPointCloud) {
    fileTypesByCategory.PointCloud = PcFileExtensionsList;

    cards.push(
      <ImportCard
        ariaLabel="import cloud"
        icon={<PointCloudIcon sx={{ width: "40px", height: "40px" }} />}
        title="Point clouds"
        description="Upload datasets that have already been aligned, cleaned, and are ready for measurement or analysis."
        footer={
          <Stack>
            <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
              {PcFileExtensionsList.join(", ")}
            </FaroText>
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
            >
              <FaroText variant="bodyS">
                Max size:{" "}
                <TranslateVar name="maxSize">
                  {MAX_UPLOAD_CLOUD_FILE_SIZE_GB}&nbsp;GB
                </TranslateVar>
              </FaroText>
              <FileDocumentIcon sx={{ color: grey[600] }} />
            </Stack>
          </Stack>
        }
        onClick={() => {
          setTypeOfFileToUpload(TypeOfFileBeingUploaded.pointcloud);
          if (fileInput.current) {
            const validExtensions = [];
            validExtensions.push(...PcFileExtensionsList);

            // Update the file input filter with only supported pointcloud extensions
            Object.assign(fileInput.current, {
              accept: validExtensions,
            });
            onUploadClicked();
          }
        }}
      />,
    );
  }

  if (projectId && canUploadWithDataManagement) {
    cards.push(<ImportScansCard from="importScansDialog" />);
  }

  if (canImportGaussianSplats) {
    fileTypesByCategory.GaussianSplat = GaussianSplatsFileExtensionsList;

    cards.push(
      <ImportCard
        ariaLabel="import splats"
        icon={<GaussianSplattingIcon sx={{ width: "40px", height: "40px" }} />}
        title="Gaussian splats"
        description="Import 3D Gaussian splat reconstructions for photorealistic, high-fidelity visualizations."
        footer={
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
              {GaussianSplatsFileExtensionsList.join(", ")}
            </FaroText>
            <FileDocumentIcon sx={{ color: grey[600] }} />
          </Stack>
        }
        onClick={() => {
          setTypeOfFileToUpload(TypeOfFileBeingUploaded.gaussiansplats);
          if (fileInput.current) {
            const validExtensions = [];

            validExtensions.push(...GaussianSplatsFileExtensionsList);

            // Update the file input filter with only supported gaussian splats extensions
            Object.assign(fileInput.current, {
              accept: validExtensions,
            });

            onUploadClicked();
          }
        }}
      />,
    );
  }

  if (canImportCAD) {
    fileTypesByCategory.CadModel = SUPPORTED_CAD_EXTENSIONS_FOR_DRAG_AND_DROP;

    cards.push(
      <ImportCard
        ariaLabel="import cad"
        icon={<CadSmallIcon sx={{ width: "40px", height: "40px" }} />}
        title="3D models"
        description="Bring in architectural, engineering, or product models from your design tools."
        footer={
          <Stack>
            <FaroText
              onClick={displayFileSizeRecommendations}
              color={cyan[400]}
              variant="bodyS"
              sx={{
                ":hover": { textDecoration: "underline" },
              }}
            >
              Recommended file size by format
            </FaroText>

            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
            >
              <FaroText
                onClick={displayListOfSupportedCadFormats}
                color={cyan[400]}
                variant="bodyS"
                sx={{
                  ":hover": { textDecoration: "underline" },
                }}
              >
                List of supported formats
              </FaroText>
              <FileDocumentIcon sx={{ color: grey[600] }} />
            </Stack>
          </Stack>
        }
        onClick={() => {
          setTypeOfFileToUpload(TypeOfFileBeingUploaded.cad);

          if (fileInput.current) {
            // Update the file input filter with only supported CAD extensions
            Object.assign(fileInput.current, {
              accept: CADFileExtensionsListForFileInput,
            });
            onUploadClicked();
          }
        }}
      />,
    );
  }

  fileTypesByCategory.Area = ImgSheetFileExtensionsWithDotsList;

  cards.push(
    <ImportCard
      ariaLabel="import area"
      icon={<AreaIcon sx={{ width: "40px", height: "40px" }} />}
      title="Areas"
      description="Create new areas by uploading 2D plans, layouts, or schematics."
      footer={
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
            {ImgSheetFileExtensionsWithDotsList.join(", ")}
          </FaroText>
          <FileDocumentIcon sx={{ color: grey[600] }} />
        </Stack>
      }
      onClick={() => {
        Analytics.track(EventType.startImportAreaWorkflow);
        onClose();
        // Reset the file from a previous drag & drop action.
        setSheetFile(undefined);
        dispatch(resetImportSheetAreaId());
        dispatch(changeMode("importSheet"));
      }}
    />,
  );

  if (activeArea) {
    fileTypesByCategory.Sheet = ImgSheetFileExtensionsWithDotsList;

    cards.push(
      <ImportCard
        ariaLabel="import sheet"
        icon={<LayersIcon sx={{ width: "40px", height: "40px" }} />}
        title="Sheets"
        description="Add sheets to the current area by uploading 2D plans, layouts, or schematics."
        footer={
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
              {ImgSheetFileExtensionsWithDotsList.join(", ")}
            </FaroText>
            <FileDocumentIcon sx={{ color: grey[600] }} />
          </Stack>
        }
        onClick={() => {
          Analytics.track(EventType.startImportSheetWorkflow);
          onClose();
          // Reset the file from a previous drag & drop action.
          setSheetFile(undefined);
          dispatch(setImportSheetAreaId(activeArea.id));
          dispatch(changeMode("importSheet"));
        }}
      />,
    );
  }

  if (hasImportAnnotationsFeature) {
    fileTypesByCategory.Annotation = AnnotationFileExtensionsList;

    cards.push(
      <ImportCard
        ariaLabel="import annotations"
        icon={<MarkupPolygonIcon sx={{ width: "40px", height: "40px" }} />}
        title="Annotations"
        description="Import annotations from a CSV file."
        footer={
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
              {AnnotationFileExtensionsList.join(", ")}
            </FaroText>
            <FileDocumentIcon sx={{ color: grey[600] }} />
          </Stack>
        }
        onClick={async () => {
          onClose();
          await importAnnotations();
        }}
      />,
    );
  }

  if (hasUnassigned2dPhotosFeature) {
    fileTypesByCategory.Photo = PhotoFileExtensionsList;

    cards.push(
      <ImportCard
        ariaLabel="import unassigned 2D photos"
        icon={<FileImageIcon sx={{ width: "40px", height: "40px" }} />}
        title="2D photos"
        description="Add 2D photos to your project. You can assign them to existing or new waypoints later if needed."
        footer={
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <FaroText variant="bodyS" className={NO_TRANSLATE_CLASS}>
              {PhotoFileExtensionsList.join(", ")}
            </FaroText>
            <FileDocumentIcon sx={{ color: grey[600] }} />
          </Stack>
        }
        onClick={() => {
          onClose();
          import2DPhotos();
        }}
      />,
    );
  }

  if (projectId && canUploadWithDataManagement && canUsePhotogrammetry) {
    cards.push(<ImportDroneDataCard from="importDronesDialog" />);
  }

  return (
    <ImportBaseComponent onFileChanged={onFileChanged} fileInput={fileInput}>
      {/* Recommended file sizes dialog */}
      <Dialog
        title="Recommended 3D Model file size"
        open={isFileSizeDialogOpen}
        onConfirm={() => setIsFileSizeDialogOpen(false)}
        onClose={() => setIsFileSizeDialogOpen(false)}
        confirmText="OK"
        sx={{ zIndex: (theme) => theme.zIndex.snackbar - 3 }}
      >
        <RecommendedCadModelFileSizes />
      </Dialog>

      {/* Supported file formats dialog */}
      <Dialog
        title="Supported 3D model formats"
        open={isFileFormatsDialogOpen}
        onConfirm={() => setIsFileFormatsDialogOpen(false)}
        onClose={() => setIsFileFormatsDialogOpen(false)}
        confirmText="OK"
        sx={{ zIndex: (theme) => theme.zIndex.snackbar - 3 }}
      >
        <ListSupportedCadFormats />
      </Dialog>

      {/* Overlay dialog with import cards */}
      <Dialog
        title="Bring your data into the cloud"
        open={open}
        onClose={onClose}
        size="l"
        sx={{
          // Set the z-index to achieve the following stacking of UI elements:
          //  1) Toasts: snackbar = 1400
          //  2) MainUi > ModalSpinner: snackbar - 1 = 1399
          //  3) Data Management > DataCategoryDialog: snackbar - 2 = 1398
          //  4) ImportDataDialog > Dialog: snackbar - 3 = 1397
          //  5) NewFeaturePopup > FaroPopover: modal + 1 = 1301
          zIndex: (theme) => theme.zIndex.snackbar - 3,
          "& .MuiDialog-container > .MuiPaper-root": {
            // If one of the sub-dialogs is open, hide this dialog so that not two overlay dialogs
            // are active.
            display:
              isFileSizeDialogOpen || isFileFormatsDialogOpen
                ? "none"
                : undefined,
            backgroundColor: grey[50],
            borderRadius: 4,
          },
        }}
      >
        {currentUser && companyId && projectId ? (
          <DataManagementDropzone
            currentUser={currentUser}
            companyId={companyId}
            projectId={projectId}
            fileTypesByCategory={fileTypesByCategory}
            isChecking={isCheckingFiles}
            setIsChecking={setIsCheckingFiles}
            onFinished={onFinishedCheckingFiles}
          />
        ) : null}

        <FaroText variant="bodyL" color={grey[600]} sx={{ mb: 3 }}>
          Drag & drop your files or choose the type of data you want to import.
        </FaroText>

        <Grid container spacing={2} rowSpacing={2} sx={{ px: 1, mb: 1 }}>
          {cards.map((item, index) => (
            <Grid key={index} size={isMdUp ? 4 : 6}>
              {item}
            </Grid>
          ))}
        </Grid>
      </Dialog>
    </ImportBaseComponent>
  );
}

/**
 * properties to be passed to ImportBaseComponent from ImportDataDialog
 */
type ImportBaseComponentProps = {
  /** list of file extensions shown if filter of OpenFile dialog */
  acceptedExtensions?: string;

  /** callback to be executed at the selection of the file in dialog */
  onFileChanged(args: OnFileChangedArgs): void;

  /** ref to file context */
  fileInput: RefObject<HTMLInputElement | null>;
};

/**
 * @returns component to provide base functionality for ImportDataDialog
 */
function ImportBaseComponent({
  children,
  acceptedExtensions,
  onFileChanged,
  fileInput,
}: PropsWithChildren<ImportBaseComponentProps>): JSX.Element | null {
  const { handleErrorWithToast } = useErrorHandlers();

  // if the user has permission to edit the project or not
  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const onFileChangedHandler = useCallback(() => {
    if (fileInput.current) {
      const selectedFile = fileInput.current.files?.[0];
      if (!selectedFile) return;

      const extension =
        `.${getFileExtension(selectedFile.name)}`.toLowerCase() || "";

      onFileChanged({
        selectedFile,
        extension,
        onSuccess: () => {
          // @ts-expect-error this line is needed in chrome to reset the input field
          // or it won't trigger again the onChange even if the user select the same file
          // a second time
          fileInput.current.value = null;
        },
        onFailedUpload: () =>
          handleErrorWithToast({
            title: "Failed to import file",
            error: "The file extension is not supported.",
          }),
      });
    }
  }, [fileInput, onFileChanged, handleErrorWithToast]);

  // If the user does not have write permission, do not show anything.
  if (!hasWritePermission) {
    return null;
  }

  return (
    <EventBarrier>
      <Box component="div">
        <input
          type="file"
          ref={fileInput}
          accept={acceptedExtensions}
          style={{ display: "none" }}
          onChange={onFileChangedHandler}
        />

        {children}
      </Box>
    </EventBarrier>
  );
}
